import { Box, Button, Divider, Flex, Icon, Text } from '@chakra-ui/react';
import { useContext } from 'react';
import { CO2 } from '../../business-emissions/Components/CO2';
import { createParamsFromInput } from '../../util/getBusinessDefaultURL';
import { roundDecimals } from '../../util/roundNumber';
import { EstimationInput, EstimationPresets } from '../App';
import {
  EmissionCalculationInTonnes,
  getEmissionCalculation,
} from '../configs/industryCalculationTemplate';
import { WidgetContext } from '../context/Widget-context';

type Props = {
  onNext: (emissionTonnes: EmissionCalculationInTonnes) => void;
  onReset: () => void;
  estimationInputValues: EstimationInput;
  estimationPresets: EstimationPresets;
  calculateEmissionsHref: string;
};

export const EstimationResult = ({
  onNext,
  onReset,
  estimationPresets,
  estimationInputValues,
  calculateEmissionsHref,
}: Props): JSX.Element => {
  const { countryCode, regionKey } = useContext(WidgetContext);
  const emissionCalculation = getEmissionCalculation(
    estimationInputValues,
    countryCode,
    regionKey,
  );

  const params = createParamsFromInput(estimationInputValues);
  const offsetNowHref = `${calculateEmissionsHref}&${params}`;

  const onOffsetClick = () => {
    onNext(emissionCalculation);
  };
  return (
    <>
      <Box textAlign="center" pb="10">
        <Icon
          viewBox="0 0 67 43"
          width="100px"
          height="100px"
          color="brand.colorPrimary"
        >
          <path
            d="M58.6 23.8C58.6 23.7 58.6 23.6 58.6 23.5C58.6 20.1 56.5 17.1 53.3 16C53.4 15.5 53.4 15 53.4 14.5C53.4 9.5 49.3 5.4 44.3 5.4C43.4 5.4 42.5 5.5 41.6 5.9C39.8 2.2 36.3 0 32.3 0C27.5 0 23.3 3.3 22.1 8C20 8.2 18 9.2 16.7 10.7C15.2 12.3 14.5 14.5 14.8 16.9C14.7 17 14.6 17.1 14.4 17.3C13.4 16.6 12.3 16.3 11.1 16.3C8 16.3 5.5 18.8 5.4 21.8C2.3 22.4 0 25.1 0 28.3C0 31.9 2.9 34.9 6.6 34.9H20.8C21.6 39.3 25.5 42.5 30 42.5H57.5C62.7 42.5 66.9 38.3 66.9 33.1C66.9 28.4 63.3 24.4 58.6 23.8ZM24.2 9.2C24.9 5.1 28.3 2.2 32.3 2.2C35.7 2.2 38.7 4.3 39.9 7.6L40.3 8.7L41.4 8.2C42.1 7.9 43.1 7.5 44.3 7.5C48.1 7.5 51.2 10.6 51.2 14.4C51.2 14.7 51.2 15.1 51.1 15.5C50.9 15.5 50.8 15.5 50.6 15.5C48.8 15.5 47 16.2 45.6 17.3C43.6 14.4 40.3 12.7 36.9 12.7C32.9 12.7 29.4 14.8 27.5 18.3C25.3 14.8 22.3 14.4 20.2 14.4C19.1 14.4 18 14.7 17 15.2C17.2 14 17.6 13 18.3 12.2C19.5 10.9 21.1 10.2 23.1 10.2H24.1L24.2 9.2ZM20.7 32.6H6.6C4.2 32.6 2.3 30.6 2.3 28.3C2.3 27.2 2.8 26.1 3.6 25.3C4.3 24.4 5.4 24 6.6 24L8 24.1L7.8 22.7C7.8 22.4 7.7 22.1 7.7 22C7.7 20.1 9.2 18.6 11.1 18.6C12.1 18.6 13.1 19.1 13.7 19.8L14.8 21.1L15.6 19.6C16.5 17.8 18.3 16.7 20.2 16.7C22.4 16.7 24.8 17.2 26.4 21.2C26.2 21.9 26.2 22.7 26.2 23.5C26.2 23.9 26.2 24.3 26.2 24.7C23 26 20.8 29.2 20.7 32.6ZM57.5 40.3H30C26.1 40.3 22.9 37.1 22.9 33.2C22.9 30.1 24.9 27.4 27.8 26.5L28.8 26.2L28.5 25.2C28.4 24.7 28.4 24.2 28.4 23.6C28.4 18.9 32.2 15.1 36.9 15.1C40 15.1 42.9 16.9 44.4 19.8L45.2 21.3L46.3 20C47.4 18.7 49 17.9 50.7 17.9C53.9 17.9 56.4 20.5 56.4 23.6C56.4 23.9 56.3 24.5 56.3 24.8L56 26.1H57.5C61.4 26.1 64.6 29.3 64.6 33.2C64.6 37.1 61.4 40.3 57.5 40.3Z"
            fill="currentColor"
          />
        </Icon>
        <Text as="p" fontSize="lg">
          Your emissions estimate is
        </Text>
        <Text as="p" fontSize="5xl" fontWeight="700">
          {roundDecimals(emissionCalculation)}
          <abbr title="tonnes">T</abbr>
        </Text>
        <Text as="p" fontSize="lg" pb="6">
          of <CO2 /> per year
        </Text>

        <Text fontSize="sm" pb="2">
          This estimate is based on the average{' '}
          {estimationInputValues.industryId} business with{' '}
          {estimationInputValues.employees}{' '}
          {
            estimationPresets.emittorLabels[estimationInputValues.industryId]
              .unit
          }
          .
        </Text>
        <Button
          onClick={onReset}
          variant="link"
          color="brand.colorPrimary"
          _hover={{ color: 'brand.colorPrimary' }}
        >
          Recalculate
        </Button>
      </Box>

      <Flex
        fontSize="md"
        direction={{ base: 'column', md: 'row' }}
        textAlign={{ base: 'center', md: 'left' }}
      >
        <Box flex="0.45">
          <Text as="p">
            For a more accurate calculation of your overall business emissions
            try our{' '}
            <Button
              as="a"
              colorScheme="black"
              href={offsetNowHref}
              variant="link"
              target="_blank"
            >
              business calculator
            </Button>
          </Text>
        </Box>
        <Box flex="0.10">
          <Flex
            height="100%"
            direction={{ base: 'row', md: 'column' }}
            justify="center"
            align="center"
          >
            <Divider
              orientation="vertical"
              display={{ base: 'none', md: 'block' }}
              borderColor="#C4C4C4"
              flex="1"
            />
            <Divider
              orientation="horizontal"
              display={{ base: 'block', md: 'none' }}
              borderColor="#C4C4C4"
              flex="1"
            />

            <Text
              as="p"
              margin={{ base: '10px', md: '0' }}
              marginBottom={{ base: '10px', md: '5px' }}
              fontWeight="600"
            >
              or
            </Text>

            <Divider
              flex="1"
              display={{ base: 'none', md: 'block' }}
              borderColor="#C4C4C4"
              orientation="vertical"
            />
            <Divider
              orientation="horizontal"
              display={{ base: 'block', md: 'none' }}
              borderColor="#C4C4C4"
              flex="1"
            />
          </Flex>
        </Box>
        <Box flex="0.45" textAlign="left">
          <Text as="p" mb="2.5">
            Offset my emissions based on the industry average.
          </Text>
          <Button onClick={onOffsetClick} target="_top">
            Offset now
          </Button>
        </Box>
      </Flex>
    </>
  );
};
